import { Boxes } from '@orgus/ui/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Panel, Pressable, Segmented, cn, formatInt } from '@orgus/ui';
import { useAllSessions, useOrgus } from '../../lib/store.ts';
import { useSessionParam } from '../sessions/useSessionParam.ts';
import { fieldGroups, fieldMotion } from './live.ts';
import type { FieldCell, FieldGrouping } from './live.ts';
import { useNeedsYouState } from '../asks/needsYou.ts';
import { blockedOnly } from '../asks/levels.ts';

const CELL: Record<FieldCell['state'], string> = {
  'needs-you': 'bg-status-attention-fill',
  failed: 'bg-status-danger-fill',
  working: 'bg-status-working-fill',
  // Quiet, but a mark on both themes: bg-active vanished on the dark panel.
  idle: 'bg-fg-faint',
};
/** The ripple's colour: the cell's own state colour (currentColor in motion-ping). */
const INK: Record<FieldCell['state'], string> = {
  'needs-you': 'text-status-attention-fill',
  failed: 'text-status-danger-fill',
  working: 'text-status-working-fill',
  idle: 'text-fg-faint', // only on the aria-hidden ripple, never text
};
const WORD: Record<FieldCell['state'], string> = {
  'needs-you': 'Needs you',
  failed: 'Failed',
  working: 'Working',
  idle: 'Idle',
};

/** Past this many agents the cells shrink, so a thousand still fit the panel. */
const DENSE_ABOVE = 400;
/** A group shows this many cells before it folds the rest behind "+N", and the panel this many groups. */
const GROUP_CELLS = { normal: 96, dense: 240 };
const GROUPS = 8;

function useGrouping(): [FieldGrouping, (g: FieldGrouping) => void] {
  const [by, setBy] = useState<FieldGrouping>(() => {
    try {
      return localStorage.getItem('orgus.field.by') === 'machine' ? 'machine' : 'project';
    } catch {
      return 'project';
    }
  });
  return [
    by,
    (g) => {
      setBy(g);
      try {
        localStorage.setItem('orgus.field.by', g);
      } catch {
        // Storage blocked: the choice lasts for this visit.
      }
    },
  ];
}

/**
 * Every agent at once, at any scale: one small square per session and a round one per subagent,
 * grouped by project or by machine and coloured by state. Hundreds fit in one panel and past
 * 400 the cells shrink; a big group folds behind "+N" and the busiest groups come first.
 * Hovering one names it in the line underneath, and a click opens its session.
 */
export function AgentField({ className }: { className?: string }) {
  const sessions = useAllSessions();
  const projects = useOrgus((s) => s.projects);
  const machines = useOrgus((s) => s.machines);
  const [, openSession] = useSessionParam();
  const [by, setBy] = useGrouping();
  // Amber only for what blocks (ADR 0056): an asked question is a quiet cell.
  const levels = useNeedsYouState().bySession;
  const groups = useMemo(() => fieldGroups(blockedOnly(sessions, levels), by), [sessions, by, levels]);
  const [hover, setHover] = useState<FieldCell | null>(null);
  const [opened, setOpened] = useState<ReadonlySet<string>>(new Set());
  const [allGroups, setAllGroups] = useState(false);
  const all = useMemo(() => groups.flatMap((g) => g.cells), [groups]);
  // The stamps last drawn: only what changed since moves (fieldMotion).
  const drawn = useRef<Map<string, string> | null>(null);
  const motion = useMemo(() => fieldMotion(drawn.current, all), [all]);
  useEffect(() => {
    drawn.current = new Map(all.map((c) => [c.id, c.stamp]));
  }, [all]);
  const dense = all.length > DENSE_ABOVE;
  const cap = dense ? GROUP_CELLS.dense : GROUP_CELLS.normal;
  const name = (key: string | null) => (by === 'machine' ? (machines[key ?? '']?.name ?? 'Unknown machine') : key ? (projects[key]?.name ?? 'Unknown project') : 'No project');
  const shownGroups = allGroups ? groups : groups.slice(0, GROUPS);
  const toggle = (id: string) =>
    setOpened((o) => {
      const n = new Set(o);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const count = (st: FieldCell['state']) => all.filter((c) => c.state === st).length;
  const legend: Array<[FieldCell['state'], number]> = (['needs-you', 'working', 'failed', 'idle'] as const).map((st) => [st, count(st)]);

  return (
    <Panel
      icon={<Boxes />}
     
      title="Agents"
      sub={formatInt(all.length)}
      className={className}
      actions={
        all.length > 0 ? (
          <Segmented<FieldGrouping>
            label="Group by"
            value={by}
            onChange={setBy}
            options={[
              { value: 'project', label: 'Project' },
              { value: 'machine', label: 'Machine' },
            ]}
          />
        ) : undefined
      }
    >
      {all.length === 0 ? (
        <p className="py-6 text-ui text-fg-subtle">No agent is running.</p>
      ) : (
        <div className="flex flex-col gap-4">
          {/* The legend names both encodings: colour is the state, shape is what it is. */}
          <ul className="flex flex-wrap gap-x-4 gap-y-1" aria-label="Agents by state">
            {legend
              .filter(([, n]) => n > 0)
              .map(([st, n]) => (
                <li key={st} className="flex items-center gap-2 text-caption text-fg-muted">
                  <span aria-hidden className={cn('h-1 w-3 rounded-pill', CELL[st])} />
                  {WORD[st]}
                  <span className="text-fg-subtle num">{formatInt(n)}</span>
                </li>
              ))}
            <li aria-hidden className="h-4 w-px self-center bg-border-subtle max-sm:hidden" />
            <li className="flex items-center gap-2 text-caption text-fg-subtle">
              <span aria-hidden className="size-2 rounded-none bg-fg-subtle" />
              Session
            </li>
            <li className="flex items-center gap-2 text-caption text-fg-subtle">
              <span aria-hidden className="size-2 rounded-pill bg-fg-subtle" />
              Subagent
            </li>
          </ul>
          {/* The groups flow side by side as wide as their marks, sharing the row, so a small group
              never takes a row of its own while the panel's width goes unused. */}
          <div className="flex max-h-96 flex-wrap content-start gap-x-6 gap-y-3 overflow-y-auto" onMouseLeave={() => setHover(null)}>
            {shownGroups.map((g) => {
              const id = g.key ?? 'none';
              const open = opened.has(id);
              const cells = open ? g.cells : g.cells.slice(0, cap);
              const asks = g.cells.filter((c) => c.state === 'needs-you').length;
              return (
                <section key={id} className="flex min-w-0 max-w-full grow basis-40 flex-col gap-1" aria-label={name(g.key)}>
                  <span className="flex items-baseline justify-between gap-2 text-caption">
                    <span className="truncate text-fg-muted">{name(g.key)}</span>
                    <span className="shrink-0 num">
                      {asks > 0 && <span className="text-status-attention">{formatInt(asks)} {asks === 1 ? 'needs' : 'need'} you · </span>}
                      <span className="text-fg-subtle">{formatInt(g.cells.length)}</span>
                    </span>
                  </span>
                  {/*
                    A picture of every agent for the pointer (hover names it, click opens it). Too dense
                    for 24 px targets, so it is hidden from keyboard and screen readers: Live now lists
                    the same agents as full rows, and the legend says the counts. No list markup: hidden
                    from assistive tech, an item per mark only added elements (1,600 at scale).
                  */}
                  <div className="flex flex-wrap items-center gap-1" aria-hidden>
                    {cells.map((c) => (
                        <span
                          key={c.id}
                          onMouseEnter={() => setHover(c)}
                          onClick={() => openSession(c.sessionId)}
                          title={`${WORD[c.state]}: ${c.label}`}
                          className={cn(
                            'block cursor-pointer transition-transform duration-(--duration-fast) hover:scale-125',
                            motion.fresh.has(c.id) && 'motion-grow',
                            dense ? 'size-2' : 'size-3',
                            c.kind === 'subagent' ? 'rounded-pill' : 'rounded-none',
                            CELL[c.state],
                          )}
                        >
                          {/* Each time the agent does something, one soft ripple (within the live budget). */}
                          {motion.ripple.has(c.id) && (
                            <span
                              key={c.stamp}
                              aria-hidden
                              className={cn('motion-ping pointer-events-none block size-full', c.kind === 'subagent' ? 'rounded-pill' : 'rounded-none', INK[c.state])}
                            />
                          )}
                        </span>
                    ))}
                  </div>
                  {g.cells.length > cap && (
                    <Pressable
                      onClick={() => toggle(id)}
                      className="self-start rounded-control text-caption text-fg-subtle transition-colors duration-(--duration-fast) hover:text-fg num"
                    >
                      {open ? 'Show less' : `${formatInt(g.cells.length - cap)} more`}
                    </Pressable>
                  )}
                </section>
              );
            })}
            {groups.length > GROUPS && (
              <div className="flex basis-full">
                <Button variant="ghost" size="sm" onClick={() => setAllGroups((x) => !x)}>
                  {allGroups ? 'Show the busiest' : `Show ${formatInt(groups.length - GROUPS)} more ${by === 'machine' ? 'machines' : 'projects'}`}
                </Button>
              </div>
            )}
          </div>
          {/* The mark under the pointer, named; quiet at rest (its title says it too). */}
          <p className="min-h-4 truncate text-caption text-fg-subtle" aria-live="polite">
            {hover && (
              <>
                <span className="text-fg-muted">{WORD[hover.state]}</span>
                {` · ${hover.kind === 'subagent' ? 'Subagent' : 'Session'} · ${hover.label}`}
              </>
            )}
          </p>
        </div>
      )}
    </Panel>
  );
}
